import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  useCountBuyerEventsByStatusQuery,
  useGetContractStatisticsQuery,
  useGetPortalFrameworkAgreementsQuery,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { keepsReportsWith, type SubAccess, useSub } from "../subscription/useSub";
import type { AppRole } from "./navConfig";

export interface OwnedWork {
  sourcingEvents: boolean;
  contracts: boolean;
  frameworkPanels?: boolean;
}

export const withOwnedWork = (sub: SubAccess, owned: OwnedWork): SubAccess => {
  const access = {
    ...sub,
    canViewSourcingEvents: sub.canViewSourcingEvents || owned.sourcingEvents,
    canViewContracts: sub.canViewContracts || owned.contracts,
    canViewContractOverview: sub.canViewContractOverview || owned.contracts,
    canViewOverview: sub.canViewOverview || owned.sourcingEvents || owned.contracts,
    viewsSharedContracts: sub.viewsSharedContracts && !owned.contracts,
    seesSupplierFrameworkAgreements: sub.seesSupplierFrameworkAgreements || owned.frameworkPanels === true,
  };
  return { ...access, keepsReports: keepsReportsWith(access) };
};

export const useNavAccess = (): {
  access: SubAccess;
  owned: OwnedWork;
  resolving: boolean;
  menuResolving: boolean;
} => {
  const sub = useSub();
  const orgId = useLoggedInWithOrgContextState().selectedOrg.id;

  const lapsedSourcing = !sub.hasBuyerAccess && sub.viewsSourcingEvents;
  const lapsedContracts = !sub.hasContractAccess && sub.viewsContracts;
  const { data: eventCounts, isError: eventCountFailed } = useCountBuyerEventsByStatusQuery(
    lapsedSourcing ? { orgId } : skipToken,
  );
  const { data: contractStatistics, isError: contractStatisticsFailed } = useGetContractStatisticsQuery(
    lapsedContracts ? { orgId, organizationId: orgId } : skipToken,
  );

  const { data: panelAgreements, isError: panelAgreementsFailed } = useGetPortalFrameworkAgreementsQuery(
    sub.canBid ? skipToken : {},
  );

  const owned: OwnedWork = {
    sourcingEvents:
      lapsedSourcing && (eventCountFailed || (eventCounts?.counts ?? []).some(({ count }) => count > 0)),
    contracts: lapsedContracts && (contractStatisticsFailed || (contractStatistics?.numberOfContracts ?? 0) > 0),
    frameworkPanels: !sub.canBid && (panelAgreementsFailed || (panelAgreements?.frameworkAgreements?.length ?? 0) > 0),
  };
  const resolving =
    (lapsedSourcing && eventCounts === undefined && !eventCountFailed) ||
    (lapsedContracts && contractStatistics === undefined && !contractStatisticsFailed);
  const panelsResolving = !sub.canBid && panelAgreements === undefined && !panelAgreementsFailed;
  return { access: withOwnedWork(sub, owned), owned, resolving, menuResolving: resolving || panelsResolving };
};

export const navRoleFor = (role: AppRole, owned: OwnedWork): AppRole =>
  role === "supplier" && owned.sourcingEvents ? "orderer" : role;
